import { useEffect, useState } from 'react'

/** Trim a commit SHA to its short form for display; pass through sentinels. */
function short(sha: string | undefined): string {
  if (!sha || sha === 'unknown' || sha === 'unavailable') return sha ?? 'unknown'
  return sha.length > 7 ? sha.slice(0, 7) : sha
}

/**
 * A small fixed label showing the deployed build version (commit SHA) of the
 * web and API apps. Rendered ONLY outside production — the datadog env is
 * "development" on the test/staging deploy and "production" on prod (see the
 * deploy workflows). Reads config live from window.__APP_CONFIG__ so the gate
 * reflects the container's runtime env, and fetches the API version from the
 * anonymous /api/version endpoint (works before sign-in).
 */
export function VersionBadge() {
  const dd = window.__APP_CONFIG__?.datadog
  const env = dd?.env
  const webVersion = dd?.version
  const enabled = env !== undefined && env !== '' && env !== 'production'

  const [apiVersion, setApiVersion] = useState<string>('…')

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    fetch('/api/version')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { version?: string }) => {
        if (!cancelled) setApiVersion(d.version ?? 'unknown')
      })
      .catch(() => {
        if (!cancelled) setApiVersion('unavailable')
      })
    return () => {
      cancelled = true
    }
  }, [enabled])

  if (!enabled) return null

  return (
    <div
      role="status"
      aria-label="Deployed build versions"
      style={{
        position: 'fixed',
        bottom: 0,
        right: 0,
        zIndex: 2147483647,
        margin: 4,
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 11,
        lineHeight: 1.4,
        letterSpacing: 0.2,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        color: '#fff',
        background: 'rgba(0, 0, 0, 0.6)',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {env} · web {short(webVersion)} · api {short(apiVersion)}
    </div>
  )
}
